import { useCallback, useEffect, useRef, useState } from 'react';
import { DISCOVER_RELOAD_MS, DISCOVER_LOAD_MORE_SKELETON_COUNT } from '../utils/discover.constants';
import {
	buildPersonFeedItems,
	buildSkeletonFeedItems,
	resolveNextLoadedPersons,
} from '../utils/discoverFeedItems.utils';

const TAIL_SKELETON_PREFIX = 'tail';

export const useDiscoverFeedLoadMore = (persons, feedSessionKey) => {
	const [feedItems, setFeedItems] = useState([]);
	const appendOffsetRef = useRef(0);
	const loadTimerRef = useRef(null);
	const personsRef = useRef(persons);
	const isMountedRef = useRef(true);

	personsRef.current = persons;

	const clearLoadTimer = useCallback(() => {
		if (loadTimerRef.current) {
			window.clearTimeout(loadTimerRef.current);
			loadTimerRef.current = null;
		}
	}, []);

	const scheduleTailResolve = useCallback(() => {
		clearLoadTimer();

		loadTimerRef.current = window.setTimeout(() => {
			if (!isMountedRef.current) {
				return;
			}

			const catalog = personsRef.current;

			if (!catalog.length) {
				return;
			}

			const startIndex = appendOffsetRef.current;
			appendOffsetRef.current =
				(startIndex + DISCOVER_LOAD_MORE_SKELETON_COUNT) % catalog.length;

			setFeedItems((current) => {
				const withoutTailSkeletons = current.filter(
					(item) =>
						!(
							item.type === 'skeleton'
							&& item.key.startsWith(TAIL_SKELETON_PREFIX)
						),
				);

				return [
					...withoutTailSkeletons,
					...resolveNextLoadedPersons(catalog, startIndex),
					...buildSkeletonFeedItems(TAIL_SKELETON_PREFIX),
				];
			});

			loadTimerRef.current = null;
			scheduleTailResolve();
		}, DISCOVER_RELOAD_MS);
	}, [clearLoadTimer]);

	useEffect(() => {
		isMountedRef.current = true;
		clearLoadTimer();
		appendOffsetRef.current = 0;

		const catalog = personsRef.current;

		if (!catalog.length) {
			setFeedItems([]);
			return () => {
				isMountedRef.current = false;
				clearLoadTimer();
			};
		}

		setFeedItems([
			...buildPersonFeedItems(catalog),
			...buildSkeletonFeedItems(TAIL_SKELETON_PREFIX),
		]);
		scheduleTailResolve();

		return () => {
			isMountedRef.current = false;
			clearLoadTimer();
		};
	}, [clearLoadTimer, feedSessionKey, scheduleTailResolve]);

	return {
		feedItems,
	};
};
